/**
 * Which brand you are currently controlling, in the top bar.
 *
 * Agents act on a brand's behalf — writing in its voice, spending against its
 * campaigns — so "which brand is this?" has to be answerable at a glance rather
 * than inferred from the page. It sits beside the profile menu rather than
 * replacing it, because that avatar is the only route to settings, team, ad
 * accounts and logout.
 *
 * When the page is about a specific object the brand is dictated by it, and the
 * badge says so instead of offering a switch that the backend would ignore.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Lock } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { isCoworkLive } from '@/cowork/live/coworkClient';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';

function Mark({ name, logoUrl, size = 20 }: { name?: string; logoUrl?: string; size?: number }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="shrink-0 rounded object-contain"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded bg-[#be43ff]/12 text-[#be43ff]"
      style={{ width: size, height: size, fontSize: size * 0.5, fontWeight: 700 }}
      aria-hidden
    >
      {(name ?? '?').slice(0, 1).toUpperCase()}
    </span>
  );
}

export function BrandBadge() {
  const { brandId, brandName, brandLogoUrl, isDerived, derivedFrom, brands, selectBrand } =
    useEffectiveBrand();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!isCoworkLive() || !brandId) return null;

  const label = brandName ?? 'Brand';
  const canSwitch = !isDerived && brands.length > 1;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => canSwitch && setOpen((v) => !v)}
        disabled={!canSwitch}
        title={
          isDerived
            ? `Working on ${label} — set by the ${derivedFrom} you have open`
            : canSwitch
              ? `Working on ${label} — click to switch`
              : `Working on ${label}`
        }
        className={cn(
          'flex items-center gap-2 rounded-full border py-1 pl-1.5 pr-2.5 transition-colors',
          canSwitch
            ? 'border-gray-200 hover:border-[#be43ff]/40 hover:bg-[#be43ff]/[0.04]'
            : 'border-gray-100 cursor-default',
        )}
      >
        <Mark name={label} logoUrl={brandLogoUrl} />
        <span className="max-w-[10rem] truncate text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>
          {label}
        </span>
        {isDerived ? (
          <Lock className="h-3 w-3 shrink-0 text-gray-300" aria-hidden />
        ) : canSwitch ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        ) : null}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-[60] mt-1.5 max-h-[60vh] w-64 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
          <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#98a2b3]" style={{ fontWeight: 600 }}>
            Brand you&apos;re controlling
          </p>
          {brands.map((b) => (
            <button
              key={b.brandId}
              type="button"
              onClick={() => { selectBrand(b.brandId); setOpen(false); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50"
            >
              <Mark name={b.brandName} logoUrl={b.brandLogoUrl} size={18} />
              <span className="min-w-0 flex-1 truncate text-[13px] text-[#101828]">{b.brandName}</span>
              {b.brandId === brandId && <Check className="h-3.5 w-3.5 shrink-0 text-[#be43ff]" aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
